'use client';

import Image from '@/components/Image';
import MagicJump, { type JumpTarget } from '@/components/MagicJump';
import { useMergedRefs } from '@/hooks/useMergedRefs';
import { useValidExposure } from '@/hooks/useValidExposure';
import { useTranslations } from '@/i18n/use-translations';
import logger from '@/utils/client-logger';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useInView } from 'react-intersection-observer';

import HomeSectionHeading2026 from '../HomeSectionHeading2026/HomeSectionHeading2026';
import type {
  StyleGuideCardItem,
  StyleGuideCards2026Props,
  StyleGuideCards2026TrackingParams,
  StyleGuideCards2026ValidExposureParams,
} from './types';

const COMPONENT_ID = 'styleGuideCards2026';
const DEFAULT_HEADING_COLOR = '#1A1C1E';
const DEFAULT_TEXT_COLOR = 'var(--color-primary-white)';
//  fr 展示4个，其他6个
const MAX_CARD_COUNT = 6;
const FOUR_CARD_COUNT = 4;

const formatText = (text?: string) => text?.replace(/<br\s*\/?>/gi, '\n').trim() || '';

const getImageSize = (value: string | number | undefined, fallback: number) => {
  const size = Number(value);
  return Number.isFinite(size) && size > 0 ? size : fallback;
};

const buildJumpTarget = (item: StyleGuideCardItem): JumpTarget => ({
  jump_type: item.jump_type,
  Jump_target: item.Jump_target ?? undefined,
  ...(item.sku?.shop_sku ? { sku: { shop_sku: item.sku.shop_sku } } : undefined),
  ...(item.product_group?.id || item.product_group?.path
    ? { product_group: item.product_group }
    : undefined),
  ...(item.crowd_recommend?.id ? { crowd_recommend: { id: item.crowd_recommend.id } } : undefined),
});

const buildTrackingParams = ({
  item,
  originalIndex,
  componentId,
  moduleId,
  moduleTitle,
  moduleIndex,
}: {
  item: StyleGuideCardItem;
  originalIndex: number;
  componentId: string;
  moduleId: string;
  moduleTitle: string;
  moduleIndex: number;
}): StyleGuideCards2026TrackingParams => ({
  module_index: moduleIndex,
  module_name: componentId,
  module_id: moduleId,
  module_title: moduleTitle,
  mkt_index: originalIndex,
  mkt_index_sub: '',
  mkt_jump_type: String(item.jump_type || ''),
  mkt_jump_content: String(item.Jump_target || ''),
  module_tab_name: '',
  coupon_id_list: '',
  coupon_name_list: '',
  shop_sku: String(item.sku?.shop_sku || ''),
  business_sku: String(item.sku?.business_sku || ''),
  product_sku: String(item.sku?.sku || ''),
  product_id: String(item.sku?.id || ''),
  product_name: String(item.sku?.short_name || ''),
  group_id: String(item.product_group?.id || ''),
  group_name: String(item.product_group?.name || ''),
});

const buildValidExposureParams = ({
  item,
  originalIndex,
  componentId,
  moduleId,
  moduleTitle,
  moduleIndex,
}: {
  item: StyleGuideCardItem;
  originalIndex: number;
  componentId: string;
  moduleId: string;
  moduleTitle: string;
  moduleIndex: number;
}): StyleGuideCards2026ValidExposureParams => ({
  module_index: moduleIndex,
  module_id: moduleId,
  module_title: moduleTitle,
  module_name: componentId,
  module_tab_name: '',
  mkt_index: originalIndex,
  mkt_index_sub: '',
  mkt_jump_type: String(item.jump_type ?? ''),
  mkt_jump_content: String(item.Jump_target ?? ''),
});

function useTrackingHandlers({
  trackingParams,
  validExposureParams,
  onTrack,
  onExpTrack,
  onValidExpTrack,
}: Pick<StyleGuideCards2026Props, 'onTrack' | 'onExpTrack' | 'onValidExpTrack'> & {
  trackingParams: StyleGuideCards2026TrackingParams;
  validExposureParams: StyleGuideCards2026ValidExposureParams;
}) {
  const [exposureRef, inView] = useInView({ threshold: 0.8, triggerOnce: true });

  useEffect(() => {
    if (!inView || !onExpTrack) return;

    try {
      onExpTrack(trackingParams);
    } catch (error) {
      logger.error('2026 PC style guide card exposure tracking error:', error);
    }
  }, [inView, onExpTrack, trackingParams]);

  const handleClick = useCallback(() => {
    if (!onTrack) return;

    try {
      onTrack(validExposureParams);
    } catch (error) {
      logger.error('2026 PC style guide card click tracking error:', error);
    }
  }, [onTrack, validExposureParams]);

  const { ref: validExposureRef } = useValidExposure({
    callback: () => onValidExpTrack?.(validExposureParams),
  });

  return {
    cardRef: useMergedRefs(exposureRef, validExposureRef),
    handleClick,
  };
}

function StyleGuideCard({
  item,
  cardCount,
  originalIndex,
  componentId,
  moduleId,
  moduleTitle,
  moduleIndex,
  ctaText,
  interactionState,
  onTrack,
  onExpTrack,
  onValidExpTrack,
}: {
  item: StyleGuideCardItem;
  cardCount: number;
  originalIndex: number;
  componentId: string;
  moduleId: string;
  moduleTitle: string;
  moduleIndex: number;
  ctaText: string;
  interactionState: 'idle' | 'pointer-active';
} & Pick<StyleGuideCards2026Props, 'onTrack' | 'onExpTrack' | 'onValidExpTrack'>) {
  const trackingParams = useMemo(
    () =>
      buildTrackingParams({
        item,
        originalIndex,
        componentId,
        moduleId,
        moduleTitle,
        moduleIndex,
      }),
    [componentId, item, moduleId, moduleIndex, moduleTitle, originalIndex],
  );
  const validExposureParams = useMemo(
    () =>
      buildValidExposureParams({
        item,
        originalIndex,
        componentId,
        moduleId,
        moduleTitle,
        moduleIndex,
      }),
    [componentId, item, moduleId, moduleIndex, moduleTitle, originalIndex],
  );
  const jumpTarget = useMemo(() => buildJumpTarget(item), [item]);
  const { cardRef, handleClick } = useTrackingHandlers({
    trackingParams,
    validExposureParams,
    onTrack,
    onExpTrack,
    onValidExpTrack,
  });

  const title = formatText(item.title);
  const imageAlt = `${title.replace(/\s+/g, ' ')} Glasses`;
  const description = formatText(item.des);
  const titleColor = item.title_color?.trim() || DEFAULT_TEXT_COLOR;
  const descriptionColor = item.des_color?.trim() || titleColor;
  const shouldShowHoverDetails = cardCount !== FOUR_CARD_COUNT;

  return (
    <div ref={cardRef} className="h-full min-w-0">
      <MagicJump
        prefetch
        item={jumpTarget}
        onBeforeJump={handleClick}
        aria-label={title}
        className="group/card block h-full w-full cursor-pointer"
      >
        <article
          className={`relative h-full w-full overflow-hidden bg-auxiliary-light-gray [transform:translateZ(0)] ${
            cardCount === FOUR_CARD_COUNT ? 'rounded-[max(12px,0.625vw)]' : 'rounded-none'
          }`}
        >
          <Image
            src={item.jump_image || ''}
            alt={imageAlt}
            width={getImageSize(item.jump_img_width, 317)}
            height={getImageSize(item.jump_img_height, 555)}
            loading={moduleIndex < 3 ? 'eager' : 'lazy'}
            priority={moduleIndex < 3}
            sizes={
              cardCount === FOUR_CARD_COUNT
                ? 'calc((100vw - 114px) / 4)'
                : `${Number((100 / cardCount).toFixed(3))}vw`
            }
            className={`absolute inset-0 h-full w-full object-cover transition-transform duration-300 ease-out group-focus-visible/card:scale-[1.05] motion-reduce:transform-none motion-reduce:transition-none ${
              interactionState === 'pointer-active' ? 'scale-[1.05]' : 'scale-100'
            }`}
          />

          <div className="absolute inset-x-0 bottom-0 flex flex-col bg-[linear-gradient(0deg,rgba(0,0,0,0.4)_0%,rgba(0,0,0,0)_100%)] px-[max(16px,0.833333vw)] py-[max(20px,1.041667vw)]">
            <div className="flex flex-col gap-[max(8px,0.416667vw)]">
              <h3
                className="truncate font-bb text-[max(20px,1.4583vw)] leading-[max(24px,1.7708vw)]"
                style={{ color: titleColor }}
              >
                {title}
              </h3>
              {description ? (
                <h4
                  className="line-clamp-2 w-full whitespace-pre-line break-words font-br text-[max(12px,0.7292vw)] leading-[max(15px,0.8854vw)]"
                  style={{ color: descriptionColor }}
                >
                  {description}
                </h4>
              ) : null}
            </div>

            {shouldShowHoverDetails ? (
              <div
                className={`overflow-hidden transition-[height,opacity] duration-300 ease-out group-focus-visible/card:h-[max(56px,2.916667vw)] group-focus-visible/card:opacity-100 motion-reduce:transition-none ${
                  interactionState === 'pointer-active'
                    ? 'h-[max(56px,2.916667vw)] opacity-100'
                    : 'h-0 opacity-0'
                }`}
              >
                <span
                  aria-hidden="true"
                  className="mt-[max(16px,0.833333vw)] inline-flex h-[max(40px,2.083333vw)] w-[max(152px,7.916667vw)] items-center justify-center rounded-[max(20px,1.041667vw)] bg-primary-white font-bb text-[max(14px,0.729167vw)] leading-[max(20px,1.041667vw)] text-primary-black"
                >
                  {ctaText}
                </span>
              </div>
            ) : null}
          </div>
        </article>
      </MagicJump>
    </div>
  );
}

export default function StyleGuideCards2026({
  indexData,
  moduleIndex,
  onTrack,
  onExpTrack,
  onValidExpTrack,
}: StyleGuideCards2026Props) {
  const t = useTranslations('homePage.bestsellers2026');
  const [pointerActiveIndex, setPointerActiveIndex] = useState<number | null>(null);
  const cardListRef = useRef<HTMLUListElement>(null);
  const pointerBoundsRef = useRef<{ left: number; width: number } | null>(null);
  const lastPointerPositionRef = useRef<{ x: number; y: number } | null>(null);
  const validItems = useMemo(
    () =>
      (indexData.jump_target || [])
        .map((item, originalIndex) => ({ item, originalIndex }))
        .filter(({ item }) => Boolean(item.jump_image && formatText(item.title)))
        .slice(0, MAX_CARD_COUNT),
    [indexData.jump_target],
  );

  const updatePointerActiveCard = useCallback(
    (event: PointerEvent) => {
      if (validItems.length === FOUR_CARD_COUNT) return;

      const cardList = cardListRef.current;
      if (!cardList) return;

      const { clientX, clientY } = event;
      const lastPosition = lastPointerPositionRef.current;
      if (lastPosition?.x === clientX && lastPosition.y === clientY) return;

      lastPointerPositionRef.current = { x: clientX, y: clientY };

      let bounds = pointerBoundsRef.current;
      if (!bounds) {
        const { left, width } = cardList.getBoundingClientRect();
        if (width <= 0) return;

        bounds = { left, width };
        pointerBoundsRef.current = bounds;
      }

      const relativeX = Math.min(Math.max(clientX - bounds.left, 0), bounds.width - 1);
      const nextIndex = Math.floor((relativeX / bounds.width) * validItems.length);
      setPointerActiveIndex((currentIndex) =>
        currentIndex === nextIndex ? currentIndex : nextIndex,
      );
    },
    [validItems.length],
  );

  const handlePointerEnter = useCallback(
    (event: PointerEvent) => {
      pointerBoundsRef.current = null;
      lastPointerPositionRef.current = null;
      updatePointerActiveCard(event);
    },
    [updatePointerActiveCard],
  );

  const handlePointerLeave = useCallback(() => {
    pointerBoundsRef.current = null;
    lastPointerPositionRef.current = null;
    setPointerActiveIndex(null);
  }, []);

  useEffect(() => {
    const cardList = cardListRef.current;
    if (!cardList || validItems.length === FOUR_CARD_COUNT) return;

    const handleFocusIn = (event: FocusEvent) => {
      if (event.target instanceof Element && event.target.matches(':focus-visible')) {
        setPointerActiveIndex(null);
      }
    };
    cardList.addEventListener('focusin', handleFocusIn);
    cardList.addEventListener('pointerenter', handlePointerEnter);
    cardList.addEventListener('pointerleave', handlePointerLeave);
    cardList.addEventListener('pointermove', updatePointerActiveCard);

    return () => {
      cardList.removeEventListener('focusin', handleFocusIn);
      cardList.removeEventListener('pointerenter', handlePointerEnter);
      cardList.removeEventListener('pointerleave', handlePointerLeave);
      cardList.removeEventListener('pointermove', updatePointerActiveCard);
    };
  }, [handlePointerEnter, handlePointerLeave, updatePointerActiveCard, validItems.length]);

  if (validItems.length === 0) return null;

  const componentId = indexData.component_id || COMPONENT_ID;
  const moduleId = String(indexData.blocks_advances_id || indexData.id || '');
  const moduleTitle = indexData.layout_title || '';
  const headingTitle = formatText(indexData.layout_title);
  const headingSubtitle = formatText(indexData.layout_subtitle);
  const shouldShowHeading =
    Number(indexData.layout_title_status) === 1 && Boolean(headingTitle || headingSubtitle);
  const headingTitleColor = indexData.layout_title_color?.trim() || DEFAULT_HEADING_COLOR;

  return (
    <section data-component-id={componentId} className="w-full bg-primary-white">
      {shouldShowHeading ? (
        <HomeSectionHeading2026
          title={headingTitle}
          subtitle={headingSubtitle}
          titleColor={headingTitleColor}
          subtitleColor={
            indexData.layout_subtitle_color || indexData.layout_subtitl_color || undefined
          }
          titleTag="h1"
          subtitleTag="h5"
        />
      ) : null}

      <ul
        ref={cardListRef}
        className={`flex w-full overflow-hidden ${
          validItems.length === FOUR_CARD_COUNT
            ? 'gap-[max(6px,0.3125vw)] px-[max(48px,2.5vw)]'
            : 'h-[max(360px,28.90625vw)] gap-[max(4px,0.208333vw)]'
        }`}
      >
        {validItems.map(({ item, originalIndex }, displayIndex) => (
          <li
            key={item.id || `${formatText(item.title)}-${originalIndex}`}
            className={`min-w-0 ${
              validItems.length === FOUR_CARD_COUNT
                ? 'flex-1 aspect-[4/5]'
                : `transition-[flex-basis,flex-grow] duration-300 ease-out focus-within:grow-0 focus-within:basis-[max(288px,23.125vw)] motion-reduce:transition-none ${
                    pointerActiveIndex === displayIndex
                      ? 'grow-0 basis-[max(288px,23.125vw)]'
                      : 'flex-1'
                  }`
            }`}
          >
            <StyleGuideCard
              item={item}
              cardCount={validItems.length}
              originalIndex={originalIndex}
              componentId={componentId}
              moduleId={moduleId}
              moduleTitle={moduleTitle}
              moduleIndex={moduleIndex}
              ctaText={t('shopNow')}
              interactionState={pointerActiveIndex === displayIndex ? 'pointer-active' : 'idle'}
              onTrack={onTrack}
              onExpTrack={onExpTrack}
              onValidExpTrack={onValidExpTrack}
            />
          </li>
        ))}
      </ul>
    </section>
  );
}
